import { useState } from 'react';
import api from '@/lib/api';
import { useContactLog } from '@/hooks/useContactLog';
import { GlassModal } from './GlassModal';

const CHANNEL_LABELS: Record<string, string> = {
  call: 'call',
  whatsapp: 'WhatsApp chat',
  sms: 'text',
  email: 'email',
};

/**
 * Global prompt that appears when user returns to the app after clicking
 * a contact button. Place once near the app root.
 */
export function ContactLogPrompt() {
  const { pending, dismiss } = useContactLog();
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  if (!pending) return null;

  const channelLabel = CHANNEL_LABELS[pending.channel] ?? pending.channel;
  const title = pending.contactName
    ? `Log your ${channelLabel} with ${pending.contactName}?`
    : `Log your ${channelLabel}?`;

  const handleSave = async () => {
    setSaving(true);
    try {
      if (pending.routeStepId) {
        // Banker contact — uses dedicated endpoint that also updates last_banker_contact
        const contactType = pending.channel === 'whatsapp' ? 'call' : pending.channel === 'sms' ? 'text' : pending.channel === 'email' ? 'email' : 'call';
        await api.logBankerContact(pending.clientId, pending.routeStepId, {
          contact_type: contactType as 'call' | 'email' | 'text',
          notes: notes || undefined,
        });
      } else {
        // Client contact — log as client_communication
        const channel = pending.channel === 'whatsapp' ? 'whatsapp' : pending.channel === 'sms' ? 'sms' : pending.channel === 'email' ? 'email' : 'sms';
        await api.createCommunication(pending.clientId, {
          channel,
          direction: 'outbound',
          content: notes || `${channelLabel} contact`,
          comm_timestamp: new Date(pending.timestamp).toISOString(),
        });
      }
    } catch {
      // Silently fail — user can always log manually
    } finally {
      setSaving(false);
      setNotes('');
      dismiss();
    }
  };

  const handleDismiss = () => {
    setNotes('');
    dismiss();
  };

  return (
    <GlassModal open onClose={handleDismiss} title={title} maxWidth="sm">
      <div className="space-y-3">
        <input
          type="text"
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
          className="w-full text-sm px-3 py-2 rounded-lg border border-[var(--glass-border)] bg-white/50 focus:outline-none focus:border-[var(--brand-primary)] transition-colors"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleDismiss}
            className="px-4 py-2 text-sm rounded-lg border border-[var(--glass-border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-colors"
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-[var(--brand-primary)] text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </GlassModal>
  );
}
